import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { withRouter } from "react-router-dom";
import { get, map, includes, size } from "lodash";
import clsx from "clsx";

import { fetchGet } from "../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Tabs from "@material-ui/core/Tabs";

import CompanySites from "./CompanySites";
import CustomTab from "../Common/CustomTab";
import CompanyDevices from "./CompanyDevices";
import CompanyDetails from "./CompanyDetails";
import AssignedProfiles from "./AssignedProfiles";
import SchedulerSettings from "../Scheduler/SchedulerSettings/SchedulerSettings";

import { setBreadcrumb } from "../../actions/common";

import { getDevicesTree } from "../../utils/companies";
import { calculateChildrenCount } from "../../utils/data";

import { levels } from "../../constants/companies";
import { getQueryStringValue } from "../../utils/queryString";

const styles = {
  mainContent: {
    width: "calc(100vw - 113px)",
    height: "calc(100vh - 199px)",
    margin: "24px",
    padding: "24px",
    minHeight: 0,
    overflow: "auto",
  },
  tabBar: {
    height: "50px",
  },
  tabs: {
    height: "50px",
    float: "left",
    minHeight: "50px",
  },
};

const tabs = {
  details: "details",
  items: "items",
  profiles: "profiles",
  childs: "childs",
  devices: "devices",
  schedulerSettings: "schedulerSettings",
};

const Company = (props) => {
  const { classes, dispatch, language } = props;
  const [item, setItem] = React.useState();
  const [itemAttr, setItemAttr] = React.useState();
  const [isFetching, setIsFetching] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(tabs.details);

  const companyId = get(props, ["match", "params", "companyId"]);
  const siteId = get(props, ["match", "params", "siteId"]);
  const zoneId = get(props, ["match", "params", "zoneId"]);
  const deviceId = get(props, ["match", "params", "deviceId"]);

  const storeKey = zoneId || siteId || companyId;

  const { expandedSites } = get(props, ["companies", storeKey]) || {
    expandedSites: getQueryStringValue("expandedSites"),
  };

  const level = deviceId
    ? levels.device
    : zoneId
    ? levels.zone
    : siteId
    ? levels.site
    : levels.company;

  React.useEffect(() => {
    setItem();
    setItemAttr();
    fetchItem();
    if (level !== levels.zone && section === tabs.childs) {
      changeTab(null, tabs.details);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, siteId, zoneId, deviceId]);

  const section = get(props, ["match", "params", "tab"]) || tabs.details;

  React.useEffect(() => {
    if (level !== levels.zone && section === tabs.childs) {
      changeTab(null, tabs.details);
    } else {
      setActiveTab(get(tabs, [section]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const fetchItem = async () => {
    try {
      if (!item) {
        setIsFetching(true);
      }

      // Fetch item and item type attributes
      let res;
      if (level === levels.company) {
        res = await fetchGet(`companies/${companyId}`);
        // Fetch zones for expanded sites
        for (let i = 0; i < size(expandedSites); i++) {
          if (get(expandedSites, [i])) {
            let zones = await fetchGet(
              `companies/${companyId}/sites/${get(expandedSites, [i])}/zones`
            );
            res = {
              ...res,
              sites: map(get(res, ["sites"]), (s) =>
                get(s, ["id"]) === get(expandedSites, [i]) ? { ...s, zones } : s
              ),
            };
          }
        }
      } else if (level === levels.site) {
        res = await fetchGet(`companies/${companyId}/sites/${siteId}`);
      } else if (level === levels.zone) {
        res = await fetchGet(
          `companies/${companyId}/sites/${siteId}/zones/${zoneId}`
        );
        res.devices = map(res.devices, (d) => ({
          ...d,
          company_id: companyId,
          site_id: siteId,
          zone_id: zoneId,
          external_devices: map(get(d, ["devices"]), (ed) => ({
            ...ed,
            company_id: companyId,
            site_id: siteId,
            zone_id: zoneId,
          })),
        }));
      } else if (level === levels.device) {
        res = await fetchGet(
          `companies/${companyId}/sites/${siteId}/zones/${zoneId}/devices/${deviceId}`
        );
      }
      setItem({
        ...res,
        company_id: companyId,
        site_id: siteId,
        zone_id: zoneId,
        device_id: deviceId,
      });

      const attr = await fetchGet(
        `${level}_types/${get(res, [`${level}_type_id`])}/attributes`
      );
      setItemAttr(attr);

      if (level === levels.device) {
        res.company_name = get(res, ["zones", 0, "company", "name"]);
        res.site_name = get(res, ["zones", 0, "site", "name"]);
      }

      let breadcrumb = [
        {
          name: "admin",
          display: false,
        },
        {
          name: I18n.get("topbar_companies"),
          url: "/admin/companies",
        },
      ];
      if ([levels.site, levels.zone, levels.device].includes(level)) {
        breadcrumb.push({
          name: get(res, ["company_name"]),
          url: `/admin/companies/${companyId}/items`,
          subName: I18n.get("companies_company"),
          //keepSection: true,
        });
      }
      if ([levels.zone, levels.device].includes(level)) {
        breadcrumb.push({
          name: get(res, ["site_name"]),
          url: `/admin/companies/${companyId}/sites/${siteId}/items`,
          subName: I18n.get("common_site"),
          //keepSection: true,
        });
      }
      if ([levels.zone].includes(level) && get(res, ["parent_zone_id"])) {
        breadcrumb.push({
          name: get(res, ["parent_zone_name"]),
          url: `/admin/companies/${companyId}/sites/${siteId}/zones/${get(res, [
            "parent_zone_id",
          ])}/items`,
          subName: I18n.get("companies_zone"),
          //keepSection: true,
        });
      }
      if ([levels.device].includes(level)) {
        breadcrumb.push({
          name: get(res, ["zones", 0, "name"]),
          url: `/admin/companies/${companyId}/sites/${siteId}/zones/${zoneId}/items`,
          subName: I18n.get("companies_zone"),
          //keepSection: true,
        });
      }
      breadcrumb.push({
        name: get(res, ["name"]),
        subName:
          level === levels.company
            ? I18n.get("companies_company")
            : level === levels.site
            ? I18n.get("common_site")
            : level === levels.zone
            ? I18n.get("companies_zone")
            : I18n.get("common_device"),
      });
      dispatch(setBreadcrumb(breadcrumb));
    } catch (e) {
      console.log(e);
    }
    setIsFetching(false);
  };

  // Fetch company zones
  const fetchZones = async (companyId, siteId) => {
    try {
      setIsFetching(true);
      let res = await fetchGet(`companies/${companyId}/sites/${siteId}/zones`);
      if (level === levels.site) {
        setItem({
          ...item,
          zones: res,
        });
      } else {
        setItem({
          ...item,
          sites: map(get(item, ["sites"]), (s) =>
            get(s, ["id"]) === siteId ? { ...s, zones: res } : s
          ),
        });
      }
    } catch (e) {
      console.log(e);
    }
    setIsFetching(false);
  };

  // Fetch zone devices
  const fetchDevices = async (companyId, siteId, zoneId) => {
    try {
      setIsFetching(true);
      let res = await fetchGet(
        `companies/${companyId}/sites/${siteId}/zones/${zoneId}/devices`
      );
      let devices = getDevicesTree(res, companyId, siteId, zoneId);
      if (level === levels.company) {
        setItem({
          ...item,
          sites: map(get(item, ["sites"]), (s) =>
            get(s, ["id"]) === siteId
              ? {
                  ...s,
                  zones: map(get(s, ["zones"]), (z) =>
                    get(z, ["id"]) === zoneId
                      ? {
                          ...z,
                          devices,
                        }
                      : z
                  ),
                }
              : s
          ),
        });
      } else if (level === levels.site) {
        setItem({
          ...item,
          zones: map(get(item, ["zones"]), (z) =>
            get(z, ["id"]) === zoneId ? { ...z, devices } : z
          ),
        });
      }
    } catch (e) {
      console.log(e);
    }
    setIsFetching(false);
  };

  const getNextLevel = (level) => {
    return level === levels.company
      ? levels.site
      : level === levels.site
      ? levels.zone
      : level === levels.zone
      ? levels.device
      : "";
  };

  const changeTab = (e, v) => {
    let url = `/admin/companies/${companyId}`;
    if (siteId) url += `/sites/${siteId}`;
    if (zoneId) url += `/zones/${zoneId}`;
    if (deviceId) url += `/zones/${deviceId}`;
    if (section) url += `/${v}`;
    props.history.push(url);
  };

  const getProfileCount = () => {
    let profileTotal = get(item, ["profile_total"]) || 0;
    let profileCount = get(item, ["profile_count"]) || 0;
    return profileTotal + profileCount;
  };

  return (
    <>
      <Grid item className={clsx("titleBar", classes.tabBar)}>
        <Tabs
          value={activeTab}
          onChange={changeTab}
          indicatorColor="primary"
          centered
          className={classes.tabs}
        >
          <CustomTab
            height="50px"
            label={`${I18n.get("site_details")}`}
            value={tabs.details}
          />
          {level === levels.zone && (
            <CustomTab
              height="50px"
              label={`${I18n.get(
                `companies_${level}`
              )}s (${calculateChildrenCount(item, "childs")})`}
              value={tabs.childs}
            />
          )}
          {level !== levels.device && (
            <CustomTab
              height="50px"
              label={`${I18n.get(`companies_${getNextLevel(level)}`)}s (${
                get(item, [`${getNextLevel(level)}_count`]) || 0
              })`}
              value={tabs.items}
            />
          )}
          {includes([levels.company, levels.site], level) && (
            <CustomTab
              height="50px"
              label={`${I18n.get("companies_devices")} (${
                get(item, ["device_count"]) || 0
              })`}
              value={tabs.devices}
            />
          )}
          {level !== levels.device && (
            <CustomTab
              height="50px"
              label={`${I18n.get(
                "companies_assignedProfiles"
              )} (${getProfileCount()})`}
              value={tabs.profiles}
            />
          )}
          {/* {level === levels.site && (
            <CustomTab
              height="50px"
              label={I18n.get("scheduler_schedulerSettings")}
              value={tabs.schedulerSettings}
            />
          )} */}
        </Tabs>
      </Grid>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        className={`mainContent ${classes.mainContent}`}
      >
        {/* {!item || !itemAttr ? (
        <Grid item className="flexCenter" style={{ height: "100%" }}>
          <Loader noDecoration />
        </Grid>
      ) :  */}
        {activeTab === tabs.details && (
          <CompanyDetails
            companyId={companyId}
            siteId={siteId}
            zoneId={zoneId}
            deviceId={deviceId}
            item={item}
            itemAttr={itemAttr}
            level={level}
            fetchItem={fetchItem}
            language={language}
          />
        )}
        {activeTab === tabs.items && (
          <CompanySites
            item={item}
            companyId={companyId}
            siteId={siteId}
            zoneId={zoneId}
            deviceId={deviceId}
            level={level}
            getNextLevel={getNextLevel}
            fetchItem={fetchItem}
            fetchZones={fetchZones}
            fetchDevices={fetchDevices}
            isFetching={isFetching}
            language={language}
          />
        )}
        {activeTab === tabs.childs && (
          <CompanySites
            item={item}
            companyId={companyId}
            siteId={siteId}
            zoneId={zoneId}
            deviceId={deviceId}
            level={level}
            getNextLevel={() => levels.childZone}
            fetchItem={fetchItem}
            fetchZones={fetchZones}
            fetchDevices={fetchDevices}
            isFetching={isFetching}
            language={language}
          />
        )}
        {activeTab === tabs.devices && (
          <CompanyDevices
            companyId={companyId}
            companyName={
              level === levels.company
                ? get(item, ["name"])
                : get(item, ["company_name"])
            }
            siteId={siteId}
            language={language}
          />
        )}
        {activeTab === tabs.profiles && (
          <AssignedProfiles
            companyId={companyId}
            companyName={
              level === levels.company
                ? get(item, ["name"])
                : get(item, ["company_name"])
            }
            siteId={siteId}
            siteName={
              level === levels.site
                ? get(item, ["name"])
                : get(item, ["site_name"])
            }
            zoneId={zoneId}
            zoneName={
              level === levels.zone
                ? get(item, ["name"])
                : get(item, ["zone_name"])
            }
            language={language}
            // nextLevel={getNextLevel(level)}
            // nextLevelItems={
            //   get(item, ["zones"]) || get(item, ["sites"]) || null
            // }
          />
        )}
        {activeTab === tabs.schedulerSettings && <SchedulerSettings />}
      </Grid>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
    companies: get(state, ["companies"]),
  };
};

export default withRouter(
  connect(mapStateToProps)(withStyles(styles)(Company))
);
